import classNames from "classnames"
import { Field, Form, Formik, FormikErrors } from "formik"
import { useState } from "react"
import { stripHtml } from "string-strip-html"

import { ISupportRequest, SupporterRole, SupportRequestState } from "@api/schema"
import { ButtonProps, FormButtonArea } from "@components/common/form/FormButtonArea"
import FormikDate from "@components/common/form/FormikDate"
import FormikInput from "@components/common/form/FormikInput"
import GeneralFormError from "@components/common/form/GeneralFormError"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import { SUPPORTER_ROLE_ORDER_AS_FILTER } from "@customer-config"
import { IFormikActions } from "@redux/helper/actions"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"
import { removeNewlinesButKeepDoubles } from "@services/util"
import { IValidationErrors } from "@services/validation"

import SupporterRoleCard from "./SupporterRoleCard"

const usedNamespaces: NamespaceShortcut[] = ["common", "partner-market", "default"]

interface IProps {
  supportRequest?: ISupportRequest
  maxDaysPublished: number
  onSubmit: (values: ISupportRequest, actions: IFormikActions) => void
  submitButtonProps: ButtonProps
  cancelButtonProps?: ButtonProps
}

/**
 * This component provides a form to create and update a SupportRequest
 * based on the design: https://lindakhne358077.invisionapp.com/console/Projektfabrik-ckvm4cjpk08o601ym2p74h3u1/clq2sns1l03v5016m9l13f6dx/play
 */
const SupportRequestForm: React.FC<IProps> = (props: IProps) => {
  const {
    supportRequest,
    onSubmit,
    submitButtonProps,
    cancelButtonProps,
    maxDaysPublished
  } = props
  const t = useDynamicTranslation()

  const [chosenRole, setChosenRole] = useState<SupporterRole | null>(null)

  const maxDateForPublicUntil = new Date(new Date().getTime() + maxDaysPublished * 24 * 60 * 60 * 1000)

  const roleCards = Object.values(SUPPORTER_ROLE_ORDER_AS_FILTER).map((role: SupporterRole) =>
    (chosenRole === null || (chosenRole === role)) && <SupporterRoleCard
      key={role}
      chosen={role === chosenRole}
      supporterRole={role}
      onCancel={() => setChosenRole(null)}
      onChoice={setChosenRole}
    />
  )

  const SUPPORT_REQUEST_MAX_CONTENT_LENGTH = 600
  const SUPPORT_REQUEST_MIN_CONTENT_LENGTH = 10
  const SUPPORT_REQUEST_MAX_TITLE_LENGTH = 25

  const validateSupportRequest = (values: ISupportRequest): FormikErrors<ISupportRequest> => {
    if (values) {
      const errors: IValidationErrors = {}

      if (values.content.length > SUPPORT_REQUEST_MAX_CONTENT_LENGTH * 3 || stripHtml(values.content).result.length > SUPPORT_REQUEST_MAX_CONTENT_LENGTH) {
        errors.content = "validate.general.tooLong"
      }

      if (values.content.length < SUPPORT_REQUEST_MIN_CONTENT_LENGTH) {
        errors.content = "validate.general.tooShort"
      }

      if (values.title.length > SUPPORT_REQUEST_MAX_TITLE_LENGTH) {
        errors.title = "validate.general.tooLong"
      }

      // check for in future only, if the publicUntil date was originally set and not changed by the user
      // if it was changed, it has to be in the future to avoid history manipulation
      if (values.publicUntil && values.publicUntil !== supportRequest.publicUntil && new Date(values.publicUntil) <= new Date()) {
        errors.publicUntil = "validate.dateTime.notInFuture"
      }

      if (values.publicUntil && new Date(values.publicUntil) > maxDateForPublicUntil) {
        errors.publicUntil = "validate.dateTime.tooFarInFuture"
      }

      return errors
    }
  }

  const fieldContent = (values: ISupportRequest, role: SupporterRole): JSX.Element => {
    return <Field
      component={FormikInput}
      name={"content"}
      help={""}
      label={t("partner-market", "form.content.label")}
      placeholder={t("partner-market", "form.content.placeholder." + role)}
      maxLength={SUPPORT_REQUEST_MAX_CONTENT_LENGTH}
      minLength={SUPPORT_REQUEST_MIN_CONTENT_LENGTH}
      countLineBreaks={true}
      required
      type="textarea"
      value={values.content}
    />
  }

  const fieldTitle = (values: ISupportRequest, role: SupporterRole): JSX.Element => {
    return <Field
      component={FormikInput}
      name={"title"}
      help={""}
      label={t("partner-market", "form.title.label")}
      placeholder={t("partner-market", "form.title.placeholder." + role)}
      maxLength={SUPPORT_REQUEST_MAX_TITLE_LENGTH}
      required
      value={values.title}
    />
  }

  const formButtonArea = (
    errors: FormikErrors<ISupportRequest>,
    isSubmitting: boolean,
    excludeFields: string[]
  ): JSX.Element => <div className="text-right">
      <GeneralFormError
        errors={errors}
        excludeFields={excludeFields}
      />
      <FormButtonArea
        isSubmitting={isSubmitting}
        submitButton={submitButtonProps}
        // cancel button should reset the chosen role, if SupportRequest is being created
        // but use incoming cancelButtonProps if given
        cancelButton={cancelButtonProps || (!supportRequest && { onClick: () => setChosenRole(null) })}
      />
    </div>


  const fieldPublicUntil = (values: ISupportRequest): JSX.Element => <>
    <div className="form-text">
      {t("supportRequest", "publicUntil.header")}
      <br />(
      {t("default", "datetime.max",
        {
          value:
            // adding maxDaysPublished
            t("default", "datetime.days", { count: maxDaysPublished })
            // adding a comma
            + ", "
            // adding the latest datetime possible
            + t("default", "datetime.until", { value: t("default", "datetime.dateTime", { value: maxDateForPublicUntil }) })
        }
      )}
      )
    </div>
    <Field
      component={FormikDate}
      label=""
      name="publicUntil"
      value={values.publicUntil}
      datePickerProps={{
        showTimeSelect: true,
        // force the browser not to suggest dates (that would overlay the datepicker data)
        autoComplete: "off",
        popperPlacement: "top",
        // needed to not get behind other elements
        // @see https://stackoverflow.com/questions/64882223/im-having-trouble-with-react-datepicker-position
        portalId: "some-portal-id",
      }}
    />
  </>

  const onFormSubmit = (values: ISupportRequest, actions: IFormikActions): void => {
    // the user is not allowed to update the SupporterRole, only to set it when a new SupportRequest is created
    if (!supportRequest) {
      values.supporterRole = chosenRole
    }

    // state should not be send to the API:
    // the state is calculated from the publicUntil
    values.state = undefined

    // if no public until is given or it did not change, do not send it to the API
    // it may be null or empty, if the SupportRequest was never published and the user does not set a new publicUntil
    // it may stay the same if it was not changed
    if (!values.publicUntil || values.publicUntil === supportRequest.publicUntil) {
      values.publicUntil = undefined
    } else {
      // if public until is given, state has to be set to public
      values.state = SupportRequestState.Public
    }

    values.content = removeNewlinesButKeepDoubles(values.content)

    onSubmit(values, actions)
  }

  const formClassName = classNames(
    "common-form",
    { "new-support-request__form": !supportRequest }
  )
  const divClassName = classNames(
    // show elements side by side when creating a NEW supportRequest
    { "col": !supportRequest }
  )

  return <Formik<ISupportRequest>
    initialValues={supportRequest
      ? supportRequest
      : {
        content: "",
        supporterRole: chosenRole,
        publicUntil: undefined
      }
    }
    onSubmit={onFormSubmit}
    validate={validateSupportRequest}
    validateOnBlur={false}
    validateOnChange={false}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting,
      values,
    }) => <Form className={formClassName} onSubmit={handleSubmit}>
        {
          // if the SupportRequest is given, the form offers to update it
          supportRequest
            ? <>
              {fieldTitle(values, chosenRole)}
              {fieldContent(values, chosenRole)}
              {fieldPublicUntil(values)}
              {formButtonArea(errors, isSubmitting, ["content", "publicUntil", "title"])}
            </>
            // if the SupportRequest is NOT given, the form offers to create it
            // Show all roleCards until the user choses one; then show the input field for content.
            // The publicUntil property has to be updated seperately via Modal.
            : <>
              <div className={divClassName}>
                {roleCards}
              </div>
              {chosenRole && <div className={divClassName}>
                {fieldTitle(values, chosenRole)}
                {fieldContent(values, chosenRole)}
                {formButtonArea(errors, isSubmitting, ["content", "title"])}
              </div>}
            </>
        }
      </Form>
    }
  </Formik>
}

export default withDynamicNamespaces<IProps>(SupportRequestForm, usedNamespaces)